import React, { useState, useEffect, useRef } from 'react';
import { getEmdForTender, getTenderAuditTrail, getStatusHistory } from '../services/api';
import { formatDate } from '../utils/formatDate';
import { useModalScrollLock } from '../hooks/useModalScrollLock';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useToast } from '../context/ToastContext';
import './TenderList.css';

function formatCurrency(amount) {
  if (!amount) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

function getSourceClass(source) {
  const map = { gem: 'source-gem', cppp: 'source-cppp', state: 'source-state', private: 'source-private' };
  return map[source] || 'source-default';
}

function getStatusClass(status) {
  const map = { participating: 'status-participating', submitted: 'status-submitted', won: 'status-won', lost: 'status-lost', dropped: 'status-dropped', canceled: 'status-canceled' };
  return map[status] || 'status-default';
}

function isDeadlinePassed(dateStr) {
  return dateStr && new Date(dateStr) < new Date();
}

function isEmdExpired(dateStr) {
  return dateStr && new Date(dateStr) < new Date();
}

function isEmdExpiringSoon(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  return diffDays >= 0 && diffDays <= 7;
}

function emdStatusClass(status) {
  const map = { created: 'status-participating', submitted: 'status-submitted', locked: 'status-won', released: 'status-default', forfeited: 'status-lost', closed: 'status-default' };
  return map[status] || 'status-default';
}

function TenderViewModal({ tender, canManage, onClose, onOpenTechnicalBid, onOpenFinancialBid, onUpdateStatus }) {
  const { toast } = useToast();
  const modalRef = useRef(null);
  const [emd, setEmd] = useState(null);
  const [emdLoading, setEmdLoading] = useState(false);
  const [emdError, setEmdError] = useState(null);
  const [audits, setAudits] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [statusHistoryLoading, setStatusHistoryLoading] = useState(false);

  const isOpen = !!tender;
  useModalScrollLock(isOpen);
  useFocusTrap(modalRef, isOpen, onClose);

  useEffect(() => {
    if (!tender?.id) {
      setEmd(null);
      return;
    }
    let cancelled = false;
    setEmdError(null);
    setEmdLoading(true);
    getEmdForTender(tender.id)
      .then((res) => {
        if (!cancelled && res.success) setEmd(res.emd);
      })
      .catch((err) => { if (!cancelled) setEmdError(err.message); })
      .finally(() => { if (!cancelled) setEmdLoading(false); });
    return () => { cancelled = true; };
  }, [tender?.id]);

  useEffect(() => {
    if (!tender?.id) return;
    let cancelled = false;
    setAuditLoading(true);
    getTenderAuditTrail(tender.id)
      .then((res) => {
        if (!cancelled && res.success) setAudits(res.audits || []);
      })
      .catch(() => { if (!cancelled) setAudits([]); })
      .finally(() => { if (!cancelled) setAuditLoading(false); });
    return () => { cancelled = true; };
  }, [tender?.id]);

  useEffect(() => {
    if (!tender?.id) return;
    let cancelled = false;
    setStatusHistoryLoading(true);
    getStatusHistory(tender.id)
      .then((res) => {
        if (!cancelled && res.success) setStatusHistory(res.status_history || []);
      })
      .catch(() => { if (!cancelled) setStatusHistory([]); })
      .finally(() => { if (!cancelled) setStatusHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [tender?.id]);

  if (!tender) return null;

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tender-view-modal-title"
        className="tender-view-modal modal--md"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-btn" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div className="tender-view-content">
          <div className="tender-view-header">
            <span className={`source-badge ${getSourceClass(tender.source)}`}>
              {tender.source_display}
            </span>
            <span className={`status-badge ${getStatusClass(tender.status)}`}>
              {tender.status_display}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h2 id="tender-view-modal-title" style={{ margin: 0 }}>{tender.tender_ref}</h2>
            <button
              type="button"
              className="edit-btn"
              onClick={() => {
                if (navigator.clipboard?.writeText) {
                  navigator.clipboard.writeText(tender.tender_ref || '');
                  toast.success('Reference copied');
                }
              }}
              title="Copy reference"
              aria-label="Copy reference"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
            {tender.portal_url && (
              <a
                href={tender.portal_url}
                target="_blank"
                rel="noopener noreferrer"
                className="link-btn"
                style={{ fontSize: '13px' }}
              >
                Open in new tab
              </a>
            )}
          </div>
          {tender.title && <p className="tender-title-view">{tender.title}</p>}

          <div className="tender-details-grid">
            <div className="detail-item">
              <label>Buyer / Department</label>
              <span>{tender.buyer_name || '-'}</span>
            </div>
            <div className="detail-item">
              <label>Submission Deadline</label>
              <span className={isDeadlinePassed(tender.submission_deadline) ? 'text-danger' : ''}>
                {formatDate(tender.submission_deadline)}
              </span>
            </div>
            <div className="detail-item">
              <label>Estimated Value</label>
              <span className="value-highlight">{formatCurrency(tender.tender_value)}</span>
            </div>
            <div className="detail-item">
              <label>Products</label>
              <span>{(tender.products && tender.products.length) || 0} items</span>
            </div>
            {tender.emd_amount != null && (
              <div className="detail-item">
                <label>EMD Required</label>
                <span>{formatCurrency(tender.emd_amount)}</span>
              </div>
            )}
            {tender.won_value != null && (
              <div className="detail-item">
                <label>Won Value</label>
                <span className="value-highlight" style={{ color: '#059669' }}>{formatCurrency(tender.won_value)}</span>
              </div>
            )}
            {tender.lost_reason && (
              <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                <label>Loss Reason</label>
                <span>{tender.lost_reason}</span>
              </div>
            )}
          </div>

          {/* Pre-bid checklist */}
          {tender.pre_bid_gates && tender.pre_bid_gates.length > 0 && (
            <div
              className="pre-bid-checklist-section"
              style={{
                marginTop: '24px',
                paddingTop: '24px',
                borderTop: '1px solid var(--color-border, #e5e7eb)',
              }}
            >
              <h3 style={{ marginBottom: '8px', fontSize: '1rem', color: 'var(--color-text, #1f2937)' }}>
                Pre-bid checklist
              </h3>
              <p style={{ marginBottom: '12px', fontSize: '13px', color: 'var(--color-text-muted, #6b7280)' }}>
                {tender.pre_bid_complete ? 'All gates complete — ready to submit.' : `Completion: ${tender.pre_bid_percent ?? 0}% (${tender.pre_bid_completed_count ?? 0}/${tender.pre_bid_total_count ?? 3})`}
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {tender.pre_bid_gates.map((gate) => (
                  <li key={gate.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    {gate.completed ? (
                      <span style={{ color: 'var(--color-success, #059669)' }} aria-hidden>✓</span>
                    ) : (
                      <span style={{ color: 'var(--color-text-muted, #9ca3af)' }} aria-hidden>○</span>
                    )}
                    <span style={{ textDecoration: gate.completed ? 'none' : 'none' }}>{gate.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* EMD Section */}
        <div
          className="emd-section"
          style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid var(--color-border, #e5e7eb)',
          }}
        >
          <h3
            style={{
              marginBottom: '12px',
              fontSize: '1rem',
              color: 'var(--color-text, #1f2937)',
            }}
          >
            Earnest Money Deposit (EMD)
          </h3>
          {emdLoading && (
            <p style={{ color: 'var(--color-text-muted, #6b7280)' }}>Loading EMD...</p>
          )}
          {emdError && (
            <div className="error-message" style={{ marginBottom: '12px' }}>
              {emdError}
              <button type="button" onClick={() => setEmdError(null)}>×</button>
            </div>
          )}
          {!emdLoading && emd && (
            <>
              <div className="tender-details-grid" style={{ marginBottom: '12px' }}>
                <div className="detail-item">
                  <label>Status</label>
                  <span className={`status-badge ${emdStatusClass(emd.status)}`}>{emd.status_display}</span>
                </div>
                {emd.emd_type && (
                  <div className="detail-item">
                    <label>Type</label>
                    <span>{emd.emd_type_display || emd.emd_type}</span>
                  </div>
                )}
                {emd.emd_amount != null && (
                  <div className="detail-item">
                    <label>Amount</label>
                    <span>{formatCurrency(emd.emd_amount)}</span>
                  </div>
                )}
                {emd.beneficiary_name && (
                  <div className="detail-item">
                    <label>Beneficiary</label>
                    <span>{emd.beneficiary_name}</span>
                  </div>
                )}
                {emd.validity_end_date && (
                  <div className="detail-item">
                    <label>Validity End</label>
                    <span className={isEmdExpired(emd.validity_end_date) ? 'text-danger' : isEmdExpiringSoon(emd.validity_end_date) ? 'text-warning' : ''}>
                      {formatDate(emd.validity_end_date)}
                      {isEmdExpired(emd.validity_end_date) && ' (Expired)'}
                      {!isEmdExpired(emd.validity_end_date) && isEmdExpiringSoon(emd.validity_end_date) && ' (Expiring soon)'}
                    </span>
                  </div>
                )}
              </div>
              {(emd.dd_number || emd.dd_bank) && (
                <div className="tender-details-grid">
                  <div className="detail-item"><label>DD Number</label><span>{emd.dd_number || '-'}</span></div>
                  <div className="detail-item"><label>DD Bank</label><span>{emd.dd_bank || '-'}</span></div>
                  <div className="detail-item"><label>DD Date</label><span>{formatDate(emd.dd_date)}</span></div>
                </div>
              )}
              {(emd.bg_number || emd.bg_bank) && (
                <div className="tender-details-grid">
                  <div className="detail-item"><label>BG Number</label><span>{emd.bg_number || '-'}</span></div>
                  <div className="detail-item"><label>BG Bank</label><span>{emd.bg_bank || '-'}</span></div>
                  <div className="detail-item"><label>BG Issue Date</label><span>{formatDate(emd.bg_issue_date)}</span></div>
                </div>
              )}
              {(emd.transaction_ref || emd.payment_date) && (
                <div className="tender-details-grid">
                  <div className="detail-item"><label>Transaction Ref</label><span>{emd.transaction_ref || '-'}</span></div>
                  <div className="detail-item"><label>Payment Date</label><span>{formatDate(emd.payment_date)}</span></div>
                </div>
              )}
              {(emd.fdr_number || emd.fdr_bank) && (
                <div className="tender-details-grid">
                  <div className="detail-item"><label>FDR Number</label><span>{emd.fdr_number || '-'}</span></div>
                  <div className="detail-item"><label>FDR Bank</label><span>{emd.fdr_bank || '-'}</span></div>
                  <div className="detail-item"><label>FDR Date</label><span>{formatDate(emd.fdr_date)}</span></div>
                </div>
              )}
              {canManage && (
                <p
                  style={{
                    marginTop: '12px',
                    fontSize: '13px',
                    color: 'var(--color-text-muted, #6b7280)',
                  }}
                >
                  To create or edit this EMD, go to the <strong>EMDs</strong> tab.
                </p>
              )}
            </>
          )}
        </div>

        <div style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {tender.technical_bid_id && (
            <button type="button" className="link-btn" onClick={() => onOpenTechnicalBid?.(tender.technical_bid_id)}>
              View Technical Bid ({tender.technical_bid_status_display || tender.technical_bid_status})
            </button>
          )}
          {tender.financial_bid_id && (
            <button type="button" className="link-btn" onClick={() => onOpenFinancialBid?.(tender.financial_bid_id)}>
              View Price Bid ({tender.financial_bid_status_display || tender.financial_bid_status})
            </button>
          )}
        </div>

        {/* Status Timeline */}
        <div
          style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid var(--color-border, #e5e7eb)',
          }}
        >
          <h3 style={{ marginBottom: '12px', fontSize: '1rem', color: 'var(--color-text, #1f2937)' }}>
            Status Timeline
          </h3>
          {statusHistoryLoading && <p style={{ color: 'var(--color-text-muted, #6b7280)' }}>Loading…</p>}
          {!statusHistoryLoading && statusHistory.length === 0 && (
            <p style={{ color: 'var(--color-text-muted, #6b7280)', fontSize: '13px' }}>No status changes recorded yet.</p>
          )}
          {!statusHistoryLoading && statusHistory.length > 0 && (
            <div className="status-timeline" style={{ position: 'relative', paddingLeft: '24px' }}>
              {/* vertical line */}
              <div style={{ position: 'absolute', left: '8px', top: 0, bottom: 0, width: '2px', background: 'var(--color-border, #d1d5db)' }} />
              {statusHistory.map((h, i) => (
                <div key={h.id || i} style={{ position: 'relative', marginBottom: '16px' }}>
                  <div style={{
                    position: 'absolute', left: '-20px', top: '4px', width: '12px', height: '12px',
                    borderRadius: '50%', background: 'var(--color-primary, #b60000)', border: '2px solid var(--color-surface, #fff)',
                  }} />
                  <div style={{ fontSize: '13px' }}>
                    <strong style={{ textTransform: 'capitalize' }}>{h.previous_value || '—'}</strong>
                    <span style={{ margin: '0 4px' }}>→</span>
                    <strong style={{ textTransform: 'capitalize' }}>{h.new_value || '—'}</strong>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-muted, #6b7280)', marginTop: '2px' }}>
                    {h.performed_by_name && <span>by {h.performed_by_name} · </span>}
                    {formatDate(h.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tender Audit Trail */}
        <div
          style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid var(--color-border, #e5e7eb)',
          }}
        >
          <h3 style={{ marginBottom: '12px', fontSize: '1rem', color: 'var(--color-text, #1f2937)' }}>
            Audit Trail
          </h3>
          {auditLoading && <p style={{ color: 'var(--color-text-muted, #6b7280)' }}>Loading...</p>}
          {!auditLoading && audits.length === 0 && (
            <p style={{ color: 'var(--color-text-muted, #6b7280)' }}>No audit entries yet.</p>
          )}
          {!auditLoading && audits.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '200px', overflowY: 'auto' }}>
              {audits.map((a, i) => (
                <li
                  key={a.id || i}
                  style={{
                    padding: '8px 0',
                    borderBottom: '1px solid var(--color-border, #f3f4f6)',
                    fontSize: '13px',
                  }}
                >
                  <strong>{a.action?.replace(/_/g, ' ')}</strong>
                  {a.performed_by_name && <span> by {a.performed_by_name}</span>}
                  <span style={{ color: 'var(--color-text-muted, #6b7280)', marginLeft: '6px' }}>
                    {formatDate(a.created_at)}
                  </span>
                  {(a.previous_value || a.new_value || a.details) && (
                    <div style={{ marginTop: '4px', color: 'var(--color-text-secondary, #4b5563)', fontSize: '12px' }}>
                      {a.previous_value && a.new_value && <span>{a.previous_value} → {a.new_value}</span>}
                      {a.details && <span>{a.details}</span>}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {canManage && (
          <div className="tender-view-actions">
            <button
              type="button"
              className="edit-btn-large"
              onClick={() => onUpdateStatus?.(tender)}
            >
              Update Status
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TenderViewModal;
